import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import type { CredentialStatus } from "@/lib/credentials";

export interface CredentialRow {
  id: string;
  credential_key: string;
  status: CredentialStatus;
  earned_on: string | null;
  notes: string | null;
}

export const useCompanyCredentials = (companyProfileId: string | undefined) => {
  const { user } = useAuth();
  const [items, setItems] = useState<CredentialRow[]>([]);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user || !companyProfileId) {
      setItems([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("company_credentials")
      .select("id, credential_key, status, earned_on, notes")
      .eq("company_profile_id", companyProfileId);
    setItems((data ?? []) as CredentialRow[]);
    setLoading(false);
  }, [user, companyProfileId]);

  useEffect(() => {
    setLoading(true);
    refresh();
  }, [refresh]);

  const byKey = useMemo(() => {
    const m = new Map<string, CredentialRow>();
    items.forEach((i) => m.set(i.credential_key, i));
    return m;
  }, [items]);

  const setStatus = useCallback(
    async (credential_key: string, status: CredentialStatus | null) => {
      if (!user || !companyProfileId) {
        toast.error("Sign in to claim credentials");
        return false;
      }
      const existing = byKey.get(credential_key);
      if (status === null) {
        if (!existing) return true;
        const { error } = await supabase
          .from("company_credentials")
          .delete()
          .eq("id", existing.id);
        if (error) {
          toast.error("Could not update credential");
          return false;
        }
        await refresh();
        return true;
      }
      if (existing) {
        const { error } = await supabase
          .from("company_credentials")
          .update({
            status,
            earned_on:
              status === "earned" && !existing.earned_on
                ? new Date().toISOString().slice(0, 10)
                : existing.earned_on,
          })
          .eq("id", existing.id);
        if (error) {
          toast.error("Could not update credential");
          return false;
        }
      } else {
        const { error } = await supabase.from("company_credentials").insert({
          user_id: user.id,
          company_profile_id: companyProfileId,
          credential_key,
          status,
          earned_on: status === "earned" ? new Date().toISOString().slice(0, 10) : null,
        });
        if (error) {
          toast.error("Could not save credential");
          return false;
        }
      }
      await refresh();
      return true;
    },
    [user, companyProfileId, byKey, refresh],
  );

  const earnedCount = items.filter((i) => i.status === "earned").length;
  const inProgressCount = items.filter((i) => i.status === "in_progress").length;

  return { items, byKey, loading, setStatus, earnedCount, inProgressCount, refresh };
};